import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib';
import { Card } from '@shared/ui';
import type { AdminDashboard, AdminSpend, AdminUserSpendRow } from '../api/adminApi';
import { fmtDate, fmtInt, fmtPct, fmtRub } from '../model/format';

type SortKey =
  | 'user'
  | 'plan'
  | 'generationsToday'
  | 'generationsMonth'
  | 'rubToday'
  | 'rubAvgDay'
  | 'rubMonth'
  | 'extraRubMonth'
  | 'createdAt';

const PLAN_ORDER: Record<AdminUserSpendRow['plan'], number> = { DEMO: 0, PRO: 1, PREMIUM: 2 };

function sortValue(u: AdminUserSpendRow, key: SortKey): number | string {
  if (key === 'user') return (u.name || u.email).toLowerCase();
  if (key === 'plan') return PLAN_ORDER[u.plan] ?? 0;
  if (key === 'createdAt') return u.createdAt;
  return u[key];
}

const NUM = 'text-right tabular-nums';
const STICKY = 'sticky left-0 z-[1]';

/** Нули в денежных и счётных колонках приглушаем, чтобы взгляд цеплялся за тех, кто тратит. */
function Num({ value, children, strong }: { value: number; children: ReactNode; strong?: boolean }) {
  return <span className={cn(!value && 'text-muted-2', value > 0 && strong && 'font-medium')}>{value ? children : '—'}</span>;
}

function SpendCells({ s, days }: { s: AdminSpend; days: number }) {
  const { t } = useTranslation();
  return (
    <>
      <td className={cn(NUM, 'px-3 py-3')}><Num value={s.generationsToday}>{fmtInt(s.generationsToday)}</Num></td>
      <td className={cn(NUM, 'px-3 py-3')}><Num value={s.generationsMonth}>{fmtInt(s.generationsMonth)}</Num></td>
      <td className={cn(NUM, 'px-3 py-3')}><Num value={s.rubToday}>{fmtRub(s.rubToday)}</Num></td>
      <td className={cn(NUM, 'px-3 py-3')} title={t('admin.table.avgHint', { days })}>
        <Num value={s.rubAvgDay}>{fmtRub(s.rubAvgDay)}</Num>
      </td>
      <td className={cn(NUM, 'px-3 py-3')}>
        <Num value={s.rubMonth} strong>{fmtRub(s.rubMonth)}</Num>
        {s.playRubMonth > 0 && (
          <div className="whitespace-nowrap text-[12px] font-normal text-muted" title={t('admin.table.playHint')}>
            {t('admin.table.play', { rub: fmtRub(s.playRubMonth) })}
          </div>
        )}
      </td>
      <td className={cn(NUM, 'px-3 py-3')}>
        {s.extraCallsMonth ? (
          <span>
            {fmtRub(s.extraRubMonth)}
            <span className="ml-1.5 text-[12px] text-muted">{t('admin.table.extraCalls', { count: s.extraCallsMonth })}</span>
          </span>
        ) : (
          <span className="text-muted-2">—</span>
        )}
      </td>
    </>
  );
}

/** Сводка «сколько генераций обошлось одним вызовом модели, а сколько — повторами». */
function CallsSummary({ data }: { data: AdminDashboard }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'ru';
  const { totals, periodDays } = data;
  const jobs = totals.jobsOneCall + totals.jobsTwoCalls + totals.jobsThreePlusCalls;
  const share = (n: number) => fmtPct(jobs ? n / jobs : 0);
  const wordsRub = totals.rubMonth - totals.playRubMonth;

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-b border-line px-5 py-3.5 text-[14px] text-muted">
      <span className="font-medium text-fg">{t('admin.calls.jobs', { days: periodDays, count: jobs })}</span>
      {jobs > 0 && (
        <>
          <span>{t('admin.calls.one', { pct: share(totals.jobsOneCall) })}</span>
          <span>{t('admin.calls.two', { pct: share(totals.jobsTwoCalls) })}</span>
          <span className={cn(totals.jobsThreePlusCalls > 0 && 'text-warn')}>
            {t('admin.calls.threePlus', { pct: share(totals.jobsThreePlusCalls) })}
          </span>
          <span>
            {t('admin.calls.extra', {
              rub: fmtRub(totals.extraRubMonth),
              // доля — от расходов на слова: игра повторов не делает и долю бы разбавила
              pct: fmtPct(wordsRub ? totals.extraRubMonth / wordsRub : 0),
            })}
          </span>
        </>
      )}
      {totals.playRubMonth > 0 && <span>{t('admin.calls.play', { rub: fmtRub(totals.playRubMonth) })}</span>}
      <span className="text-muted-2 nav:ml-auto">
        {data.measuredSince
          ? t('admin.measuredSince', { date: fmtDate(data.measuredSince, lang), rate: data.usdToRub })
          : t('admin.notMeasured')}
      </span>
    </div>
  );
}

export function AdminSpendTable({ data }: { data: AdminDashboard }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'ru';
  const days = data.periodDays;
  // По умолчанию — как отдаёт бэк: кто больше потратил за месяц, тот выше.
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'rubMonth', desc: true });

  const rows = useMemo(() => {
    const dir = sort.desc ? -1 : 1;
    return [...data.users].sort((a, b) => {
      const x = sortValue(a, sort.key);
      const y = sortValue(b, sort.key);
      if (x === y) return b.createdAt.localeCompare(a.createdAt);
      return (x < y ? -1 : 1) * dir;
    });
  }, [data.users, sort]);

  const columns: { key: SortKey; label: string; numeric?: boolean }[] = [
    { key: 'user', label: t('admin.table.user') },
    { key: 'plan', label: t('admin.table.plan') },
    { key: 'generationsToday', label: t('admin.table.genToday'), numeric: true },
    { key: 'generationsMonth', label: t('admin.table.genMonth', { days }), numeric: true },
    { key: 'rubToday', label: t('admin.table.rubToday'), numeric: true },
    { key: 'rubAvgDay', label: t('admin.table.rubAvgDay'), numeric: true },
    { key: 'rubMonth', label: t('admin.table.rubMonth', { days }), numeric: true },
    { key: 'extraRubMonth', label: t('admin.table.extra'), numeric: true },
    { key: 'createdAt', label: t('admin.table.joined'), numeric: true },
  ];

  const toggle = (key: SortKey) =>
    // Новый столбец: имя — по алфавиту, остальное — сначала большие/новые.
    setSort((s) => (s.key === key ? { key, desc: !s.desc } : { key, desc: key !== 'user' }));

  return (
    <Card padding="none" className="overflow-hidden">
      <CallsSummary data={data} />
      <div className="overflow-x-auto">
        <table className="w-full min-w-[960px] border-collapse text-[14px]">
          <thead>
            <tr className="border-b border-line">
              {columns.map((c, i) => {
                const active = sort.key === c.key;
                return (
                  <th
                    key={c.key}
                    scope="col"
                    aria-sort={active ? (sort.desc ? 'descending' : 'ascending') : 'none'}
                    className={cn(
                      'whitespace-nowrap px-3 py-3 text-[12px] font-medium uppercase tracking-[0.12em] text-muted-2',
                      c.numeric ? 'text-right' : 'text-left',
                      i === 0 && cn(STICKY, 'bg-card pl-5'),
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => toggle(c.key)}
                      className={cn('inline-flex items-center gap-1 uppercase hover:text-fg', active && 'text-fg')}
                    >
                      {c.label}
                      <span aria-hidden className={cn('text-[9px]', !active && 'opacity-0')}>
                        {sort.desc ? '▼' : '▲'}
                      </span>
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            <tr className="bg-elev font-medium">
              <td className={cn(STICKY, 'bg-elev py-3 pl-5 pr-3')}>
                {t('admin.table.total', { count: data.users.length })}
              </td>
              <td />
              <SpendCells s={data.totals} days={days} />
              <td />
            </tr>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-5 py-6 text-muted">
                  {t('admin.table.empty')}
                </td>
              </tr>
            ) : (
              rows.map((u) => (
                <tr key={u.id} className="hover:bg-elev/50">
                  <td className={cn(STICKY, 'max-w-[260px] bg-card py-3 pl-5 pr-3')}>
                    <div className="flex min-w-0 items-center gap-1.5">
                      <span className="amp-mask ym-hide-content truncate font-medium">{u.name || '—'}</span>
                      {u.role === 'ADMIN' && (
                        <span className="shrink-0 rounded-pill bg-accent/15 px-1.5 py-px text-[12px] font-medium text-accent">
                          {t('admin.table.admin')}
                        </span>
                      )}
                    </div>
                    <div className="flex min-w-0 items-center gap-1 text-[12px] text-muted">
                      <span className="amp-mask ym-hide-content truncate">{u.email}</span>
                      {!u.isEmailVerified && (
                        <span className="shrink-0 text-danger" title={t('admin.table.emailNo')}>
                          ✕
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span className="inline-flex items-center rounded-pill border border-line bg-elev px-2 py-0.5 text-[12px] font-medium">
                      {u.plan}
                    </span>
                  </td>
                  <SpendCells s={u} days={days} />
                  <td className={cn(NUM, 'whitespace-nowrap px-3 py-3 pr-5 text-[12px] text-muted')}>
                    {fmtDate(u.createdAt, lang)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
